import React, { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { loginWithPasswordWatcher, setTranslatableSuccessMessage, verifySmsMfaCode, verifyEmailMfaCode } from "../../redux/actions/loginactioncreators";
import { navigateToLogin } from "../../redux/helpers/navigators";
import OtpCodeEntry from "./MfaOtpCodeEntry";
import { MfaPageContainer } from "./MfaPageContainer.jsx";
import { mfaToastMessages } from "../../i18n/mfaToastMessages.js";
import { MfaType } from "./model/MfaType";

export default function MfaCodeEntry() {
  const dispatch = useDispatch();
  const [otpCode, setOtpCode] = useState("");
  const [otpCodeValid, setOtpCodeValid] = useState(false);

  const {
    challenge,
    mfaConfig,
    password
  } = useSelector(state => ({
    challenge: state.loginForm.challenge,
    mfaConfig: state.loginForm.mfa.config,
    password: state.loginForm.password
  }));

  const getDeliveryMedium = (challenge) => {
    let deliveryMedium = "";
    if (challenge && challenge.challengeParameters) {
      deliveryMedium = challenge.challengeParameters.deliveryMedium;
    }
    return deliveryMedium.toLowerCase();
  };

  const deliveryMedium = getDeliveryMedium(challenge);
  const otpCodeLength = mfaConfig[deliveryMedium]?.codeLength ?? 6;

  useEffect(() => {
    const message = deliveryMedium === MfaType.EMAIL ? mfaToastMessages.emailVerificationCodeSent : mfaToastMessages.smsVerificationCodeSent;
    dispatch(setTranslatableSuccessMessage(message));
  }, [challenge, deliveryMedium, dispatch]);

  const handleClickBack = () => {
    navigateToLogin();
  };

  const otpOnChange = (value) => {
    const valid = value.length === otpCodeLength;
    setOtpCode(value);
    setOtpCodeValid(valid);
  };

  const isOtpCodeValid = (value) => {
    return value.length === otpCodeLength;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (otpCodeValid) {
      deliveryMedium === MfaType.EMAIL ? dispatch(verifyEmailMfaCode(otpCode)) : dispatch(verifySmsMfaCode(otpCode));
    }
  };

  const handleResendVerificationCode = () => {
    dispatch(loginWithPasswordWatcher({
      password,
      resendOtp: true,
      deliveryMedium: deliveryMedium.toUpperCase()
    }));
  };

  const getInstructionalText = () => {
    if (deliveryMedium === MfaType.SMS) {
      return (
        <FormattedMessage
          id="mfa.smsDestination"
          defaultMessage="Please allow up to 2 minutes to receive the code. In case you didn't receive the message, be sure to check your spam filter in your messages app."
          description="Instructions for SMS MFA code verification"
        />
      );
    }
    if (deliveryMedium === MfaType.EMAIL) {
      return (
        <FormattedMessage
          id="mfa.emailDestination"
          defaultMessage="We sent a code via Email. Please allow up to 2 minutes for the code to arrive. If you didn't receive the code, please check your spam folder."
          description="Instructions for email MFA code verification"
        />
      );
    }
    return null;
  };

  return (
    <div id="mfaCode">
      <MfaPageContainer
        onSubmit={handleSubmit}
        showErrors={true}
        requiredFields={[]}
        headerText={<FormattedMessage id="mfa.codeSent" defaultMessage="We just sent you a verification code" description="Header text indicating verification code has been sent" />}
        instructionalText={getInstructionalText()}
        submitButtonText={<FormattedMessage id="mfaSetup.continueButton" defaultMessage="Continue" description="Submit OTP code" />}
        secondaryButtonText={<FormattedMessage id="mfaSetup.backButton" defaultMessage="Back" description="Return to login" />}
        secondaryButtonHandler={handleClickBack}
        resendButtonText={<FormattedMessage id="mfa.resendOtpButton" defaultMessage="Resend Code" description="A button to request another one-time password to be sent" />}
        resendButtonHandler={handleResendVerificationCode}
        onChange={otpOnChange}
        otpValidator={isOtpCodeValid}
      >
        <OtpCodeEntry id="otpCodeEntry" length={otpCodeLength} onChange={otpOnChange} />
      </MfaPageContainer>
    </div>
  );
}